import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  Card,
  Checkbox,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getSearchListContacts } from '../../services/contacts.service';
import Iconify from './iconify.component';

const ALL_OPTION = { id: 'ALL', email: 'Select All' };

const Recipients = ({
  id,
  name,
  label,
  defaultValue,
  onChange,
  disabled,
  error,
}) => {
  const selectedValue = defaultValue || [];

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState(selectedValue);

  const handleChange = (e, val) => {
    let updated = [];

    const isAll = val.some((v) => v.id === 'ALL');
    if (isAll) {
      updated = options.filter((o) => o.id !== 'ALL');
    } else {
      updated = val;
    }

    setValue(updated);
    onChange(updated);
  };

  const handleRemove = (cId) => {
    const updated = value.filter((u) => u.id !== cId);
    setValue(updated);
    onChange(updated);
  };

  useEffect(() => {
    setValue(selectedValue);
  }, [selectedValue]);

  useEffect(() => {
    getSearchListContacts()
      .then((res) => {
        const data = res.data || [];
        setOptions([ALL_OPTION, ...data]);
        setLoading(false);
      })
      .catch(() => {
        // upon error, show loading only
      });
  }, []);

  return (
    <>
      <Autocomplete
        id={id}
        name={name}
        multiple
        options={options}
        loading={loading}
        disableCloseOnSelect
        disabled={disabled}
        getOptionLabel={(option) => option.email}
        value={value}
        isOptionEqualToValue={(op, vl) => op.id === vl.id}
        onChange={handleChange}
        renderTags={() => null}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox checked={selected} />
            {option.email}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={error !== ''}
            helperText={error}
            variant="outlined"
          />
        )}
      />

      <Card variant="outlined" sx={{ p: 1, mt: 1, bgcolor: 'action.hover' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Iconify icon="ic:outline-email" color="primary.main" />
          <Typography variant="subtitle2">{`${value.length} recipients`}</Typography>
        </Stack>
      </Card>

      <Box
        mt={1}
        sx={{
          maxHeight: 220,
          overflowY: 'auto',
          border: '1px solid #ddd',
          borderRadius: 2,
          p: 1,
          bgcolor: 'action.hover',
        }}
      >
        {value.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No {label.toLowerCase()} selected
          </Typography>
        ) : (
          value.map((user) => (
            <Chip
              key={user.id}
              label={user.email}
              onDelete={() => handleRemove(user.id)}
              sx={{ m: 0.5 }}
            />
          ))
        )}
      </Box>
    </>
  );
};

Recipients.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  defaultValue: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string,
};
Recipients.defaultProps = {
  defaultValue: null,
  disabled: false,
  error: '',
};

export default Recipients;
